import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCreditCard, FiEdit2, FiEyeOff, FiMail, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiChevronDown, FiExternalLink, FiPlus, FiRefreshCw } from 'react-icons/fi';
import api from '../services/api';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import CustomerDetail from './CustomerDetail';
import PaymentModal from '../components/PaymentModal';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const getCurrentFiscalYearRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { start, end };
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: colors.stone800,
    borderRadius: 12,
    padding: 14,
    border: `1px solid ${colors.stone700}`,
  },
  statTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone300,
    marginBottom: 10,
  },
  segBar: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.stone700,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
    marginTop: 10,
  },
  legendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 11,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  legendItemActive: {
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.1)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px 7px 32px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  select: {
    padding: '7px 26px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  ...tableStyles,
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent600} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  pageBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '10px 16px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: colors.stone50,
    color: colors.stone800,
    outline: 'none',
  },
};

const getStatusDotColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID' || s === 'CONVERTED') return matte.green;
  if (s === 'SENT') return matte.purple;
  if (s === 'CANCELLED') return matte.red;
  if (s === 'DRAFT') return matte.blue;
  return matte.grey;
};

const getSourceBadge = (row) => {
  if (row?.recurringInvoiceId) return { color: colors.stone800, label: 'Recurring' };
  if (row?.quoteId) return { color: colors.stone800, label: 'Quote' };
  return { color: colors.stone700, label: 'Direct' };
};

const isProformaPaid = (row) => {
  const st = String(row?.status || '').toUpperCase();
  const paySt = String(row?.paymentStatus || '').toUpperCase();
  return st === 'PAID' || st === 'CONVERTED' || paySt === 'PAID';
};

const listBtn = {
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 8,
  height: 32,
  minHeight: 32,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

export default function Proformas() {
  const [company] = useState(() => {
    try { return JSON.parse(localStorage.getItem('company') || '{}'); } catch { return {}; }
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [actionLoading, setActionLoading] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [filters, setFilters] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    const toYMD = (iso) => new Date(iso).toISOString().slice(0, 10);
    return { status: '', number: '', fromDate: toYMD(start.toISOString()), toDate: toYMD(end.toISOString()) };
  });
  const [showStats, setShowStats] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('proformas.showStats') ?? 'true'); } catch { return true; }
  });
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Current FY' };
  });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', kind: 'Proforma', loading: false, proformaId: null });
  const [proformaPayModal, setProformaPayModal] = useState({ open: false, proformaId: null, isAdvance: false, isRecurring: false });
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, id: null, customer: null });
  const [payInfo, setPayInfo] = useState({ open: false, loading: false, proforma: null, error: '' });
  const [statusView, setStatusView] = useState('');
  const [sourceView, setSourceView] = useState('');

  const statusOptions = useMemo(() => ([
    { value: '', label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SENT', label: 'Sent' },
    { value: 'PAID', label: 'Paid' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ]), []);

  const fetchRows = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const params = {
        companyId: company.id,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        number: filters.number || undefined,
        page,
        pageSize
      };
      const { data } = await api.get('/api/proformas', { params });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setTotal(data?.total || 0);
    } catch (e) {
      setToast(e?.response?.data?.error || 'Failed to load proformas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [company?.id, page, pageSize, filters.fromDate, filters.toDate]);
  useEffect(() => { try { window.localStorage.setItem('proformas.showStats', JSON.stringify(showStats)); } catch {} }, [showStats]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 3000); return () => clearTimeout(t); }, [toast]);

  const visibleRows = useMemo(() => {
    let list = Array.isArray(rows) ? rows : [];
    const st = String(statusView || '').toUpperCase();
    if (st) {
      if (st === 'PAID') list = list.filter(r => ['PAID', 'CONVERTED'].includes(String(r?.status || '').toUpperCase()));
      else list = list.filter(r => String(r?.status || '').toUpperCase() === st);
    }
    const src = String(sourceView || '').toUpperCase();
    if (src === 'DIRECT') list = list.filter(r => !r?.quoteId && !r?.recurringInvoiceId);
    if (src === 'QUOTE') list = list.filter(r => !!r?.quoteId);
    if (src === 'RECURRING') list = list.filter(r => !!r?.recurringInvoiceId);
    return list;
  }, [rows, statusView, sourceView]);

  const rowsForStatusStats = useMemo(() => {
    let list = Array.isArray(rows) ? rows : [];
    const src = String(sourceView || '').toUpperCase();
    if (src === 'DIRECT') list = list.filter(r => !r?.quoteId && !r?.recurringInvoiceId);
    if (src === 'QUOTE') list = list.filter(r => !!r?.quoteId);
    if (src === 'RECURRING') list = list.filter(r => !!r?.recurringInvoiceId);
    return list;
  }, [rows, sourceView]);

  const rowsForSourceStats = useMemo(() => {
    let list = Array.isArray(rows) ? rows : [];
    const st = String(statusView || '').toUpperCase();
    if (st) {
      if (st === 'PAID') list = list.filter(r => ['PAID', 'CONVERTED'].includes(String(r?.status || '').toUpperCase()));
      else list = list.filter(r => String(r?.status || '').toUpperCase() === st);
    }
    return list;
  }, [rows, statusView]);

  const statsLocal = useMemo(() => {
    const byStatus = { DRAFT: 0, SENT: 0, PAID: 0, CANCELLED: 0, OTHER: 0 };
    const bySource = { DIRECT: 0, QUOTE: 0, RECURRING: 0 };
    let totalAmount = 0, paidAmount = 0, unpaidAdvanceCount = 0;
    for (const r of (rowsForStatusStats || [])) {
      const stRaw = String(r?.status || '').toUpperCase();
      const st = stRaw === 'CONVERTED' ? 'PAID' : stRaw;
      if (byStatus[st] != null) byStatus[st] += 1;
      else byStatus.OTHER += 1;
    }
    for (const r of (rowsForSourceStats || [])) {
      if (r?.recurringInvoiceId) bySource.RECURRING += 1;
      else if (r?.quoteId) bySource.QUOTE += 1;
      else bySource.DIRECT += 1;
      const paySt = String(r?.paymentStatus || '').toUpperCase();
      if (r?.quoteId && paySt !== 'PAID') unpaidAdvanceCount += 1;
    }
    for (const r of (visibleRows || [])) {
      const st = String(r?.status || '').toUpperCase();
      const tot = Number(r?.total || 0) || 0;
      totalAmount += tot;
      const paySt = String(r?.paymentStatus || '').toUpperCase();
      const isPaid = st === 'PAID' || st === 'CONVERTED' || paySt === 'PAID';
      if (isPaid) paidAmount += tot;
    }
    return {
      totalCount: (visibleRows || []).length,
      totalCountStatusBasis: (rowsForStatusStats || []).length,
      totalCountSourceBasis: (rowsForSourceStats || []).length,
      byStatus,
      bySource,
      totalAmount: +totalAmount.toFixed(2),
      paidAmount: +paidAmount.toFixed(2),
      unpaidAdvanceCount
    };
  }, [rowsForStatusStats, rowsForSourceStats, visibleRows]);

  const isStatusActive = (s) => {
    const cur = String(statusView || '').toUpperCase();
    if (s === 'ALL') return !cur;
    return cur === String(s || '').toUpperCase();
  };
  const applyStatusFilter = (s) => {
    const next = String(s || '').toUpperCase();
    const cur = String(statusView || '').toUpperCase();
    const resolved = (!next || next === 'ALL' || cur === next) ? '' : next;
    setStatusView(resolved);
    setFilters((f) => ({ ...f, status: resolved }));
  };
  const isSourceActive = (s) => {
    const cur = String(sourceView || '').toUpperCase();
    if (s === 'ALL') return !cur;
    return cur === String(s || '').toUpperCase();
  };
  const applySourceFilter = (s) => {
    const next = String(s || '').toUpperCase();
    const cur = String(sourceView || '').toUpperCase();
    setSourceView((!next || next === 'ALL' || cur === next) ? '' : next);
  };

  const formatINR = (val) => {
    try { return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const handleDateChange = (val) => {
    if (val === 'all') { setDateRange({ startDate: null, endDate: null }); setFilters((f) => ({ ...f, fromDate: '', toDate: '' })); setPage(1); return; }
    const toYMD = (iso) => new Date(iso).toISOString().slice(0, 10);
    setDateRange(val);
    setFilters((f) => ({ ...f, fromDate: toYMD(val.startDate), toDate: toYMD(val.endDate) }));
    setPage(1);
  };

  const viewPdf = async (id, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || '', kind: 'Proforma', loading: true, proformaId: id });
      const { data } = await api.get(`/api/proformas/${id}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview((p) => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false, proformaId: null }); }
  };
  const closePdfPreview = () => setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false, proformaId: null });

  const handleRegeneratePdf = async (row) => {
    const id = row?.id;
    if (!id || isProformaPaid(row)) return;
    const key = `regen-pdf:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/api/proformas/${id}/regenerate-pdf`);
      setToast('Proforma PDF regenerated');
      if (pdfPreview.open && pdfPreview.kind === 'Proforma' && pdfPreview.proformaId === id) {
        setPdfPreview((p) => ({ ...p, url: data?.url || p.url, loading: false }));
      }
    } catch (e) {
      setToast(e?.response?.data?.error || 'Failed to regenerate PDF');
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const openQuotePdfById = async (quoteId, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || 'Quote', kind: 'Quote', loading: true });
      const { data } = await api.get(`/api/quotes/${quoteId}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview((p) => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load Quote PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false }); }
  };

  const openRecurringFormInSideModal = (recurringInvoiceId, label) => {
    if (!recurringInvoiceId) return;
    setPdfPreview({ open: true, url: `/recurring-invoice/${recurringInvoiceId}/edit`, label: label || 'Recurring Invoice', kind: 'Recurring', loading: false });
  };

  const openPayModal = (row) => {
    const today = new Date().toISOString().slice(0, 10);
    setProformaPayModal({
      open: true,
      proformaId: row.id,
      isAdvance: !!row.quoteId,
      isRecurring: !!row.recurringInvoiceId,
      paymentMethod: '',
      paymentReference: '',
      paymentDate: today,
      proofUrl: '',
      uploading: false
    });
  };
  const closePayModal = () => setProformaPayModal({ open: false, proformaId: null, isAdvance: false, isRecurring: false, paymentMethod: '', paymentReference: '', paymentDate: '', proofUrl: '', uploading: false });

  const submitProformaPayment = async (paymentData) => {
    const { proformaId, isAdvance, isRecurring } = proformaPayModal;
    if (!proformaId) return;
    const key = `pay:${proformaId}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try {
      if (isAdvance) {
        await api.post(`/api/proformas/${proformaId}/payment`, { ...paymentData, paymentStatus: 'PAID' });
        setToast('Payment recorded');
      } else if (isRecurring) {
        await api.post(`/api/proformas/${proformaId}/convert-to-invoice`, paymentData);
        setToast('Invoice created');
      } else {
        await api.post(`/api/proformas/${proformaId}/payment`, { ...paymentData, paymentStatus: 'PAID' });
        setToast('Payment recorded');
      }
      setProformaPayModal({ open: false, proformaId: null, isAdvance: false, isRecurring: false });
      fetchRows();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed to record payment'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const sendEmail = async (id) => {
    const key = `send:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try { await api.post(`/api/proformas/${id}/send`); setToast('Email sent'); }
    catch { setToast('Failed to send email'); }
    finally { setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openCustomerDetail = (row) => {
    const id = row?.customerSnapshot?.id || row?.customerId || null;
    if (!id) return;
    const customer = row?.customerSnapshot ? { id, ...row.customerSnapshot } : { id, name: row?.customerSnapshot?.name || '' };
    setCustomerDrawer({ open: true, id, customer });
  };
  const closeCustomerDrawer = () => setCustomerDrawer({ open: false, id: null, customer: null });

  const closePayInfo = () => setPayInfo({ open: false, loading: false, proforma: null, error: '' });
  const openPaymentInfo = async (row) => {
    const id = row?.id;
    if (!id) return;
    const st = String(row?.status || '').toUpperCase();
    if (!(st === 'PAID' || st === 'CONVERTED')) return;
    try {
      setPayInfo({ open: true, loading: true, proforma: null, error: '' });
      const { data } = await api.get(`/api/proformas/${id}`);
      setPayInfo({ open: true, loading: false, proforma: data || null, error: '' });
    } catch (e) { setPayInfo({ open: true, loading: false, proforma: null, error: e?.response?.data?.error || 'Failed to load payment details' }); }
  };

  const totalAmt = statsLocal.totalAmount;
  const paidAmt = statsLocal.paidAmount;
  const pendingAmt = Math.max(0, totalAmt - paidAmt);

  const SegmentBar = ({ segments }) => {
    const sum = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={styles.segBar}>
        {segments.map((s, i) => (
          <button key={i} onClick={s.onClick} title={s.title} style={{ width: `${(Number(s.value || 0) / sum) * 100}%`, background: s.color, border: 'none', padding: 0, cursor: s.onClick ? 'pointer' : 'default' }} />
        ))}
      </div>
    );
  };

  const LegendItem = ({ label, value, color, active, onClick }) => (
    <button style={{ ...styles.legendItem, ...(active ? styles.legendItemActive : {}) }} onClick={onClick}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        {label}
      </span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </button>
  );

  const renderPagination = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${colors.stone100}` }}>
      <span style={{ fontSize: 11, color: colors.stone500 }}>
        Showing <strong style={{ color: colors.stone700 }}>{Math.min((page - 1) * pageSize + 1, total || visibleRows.length)}-{Math.min(page * pageSize, total || visibleRows.length)}</strong> of <strong style={{ color: colors.stone700 }}>{total || visibleRows.length}</strong>
      </span>
      <div style={styles.pagination}>
        <button style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><FiChevronLeft size={14} /></button>
        <span style={{ padding: '0 10px', fontSize: 11, color: colors.stone600 }}>Page {page}</span>
        <button style={{ ...styles.pageBtn, opacity: page * pageSize >= (total || visibleRows.length) ? 0.5 : 1 }} disabled={page * pageSize >= (total || visibleRows.length)} onClick={() => setPage(p => p + 1)}><FiChevronRight size={14} /></button>
        <select style={{ ...styles.select, marginLeft: 6 }} value={pageSize} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <style>{billingTableCSS()}</style>

      {/* Toast */}
      {!!toast && (
        <div style={styles.toast}>
          <span>{toast}</span>
          <button onClick={() => setToast('')} style={{ ...styles.btnIcon, color: '#fff' }}><FiX size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Proformas</h1>
            <p style={styles.subtitle}>{total || visibleRows.length} proformas</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          <Link
            to="/proforma/new"
            className="btn btn-secondary"
            style={{ ...listBtn, textDecoration: 'none' }}
          >
            <FiPlus size={14} /> New Proforma
          </Link>
          <HeaderDateFilters value={dateRange} onChange={handleDateChange} />
        </div>
      </div>

      {/* Stats */}
      {showStats && (
        <div style={{ ...styles.card, background: colors.stone800, border: `1px solid ${colors.stone700}` }}>
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Overview</span>
              <button style={{ ...styles.btnIcon, color: colors.stone400 }} onClick={() => setShowStats(false)} title="Hide Stats"><FiEyeOff size={14} /></button>
            </div>
            <div style={styles.statsGrid}>
              {/* Status Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Status Breakdown</div>
                <SegmentBar segments={[
                  { value: statsLocal.byStatus.DRAFT, color: matte.blue, title: 'Draft', onClick: () => applyStatusFilter('DRAFT') },
                  { value: statsLocal.byStatus.SENT, color: matte.purple, title: 'Sent', onClick: () => applyStatusFilter('SENT') },
                  { value: statsLocal.byStatus.PAID, color: matte.green, title: 'Paid', onClick: () => applyStatusFilter('PAID') },
                  { value: statsLocal.byStatus.CANCELLED, color: matte.red, title: 'Cancelled', onClick: () => applyStatusFilter('CANCELLED') },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Draft" value={statsLocal.byStatus.DRAFT} color={matte.blue} active={isStatusActive('DRAFT')} onClick={() => applyStatusFilter('DRAFT')} />
                  <LegendItem label="Sent" value={statsLocal.byStatus.SENT} color={matte.purple} active={isStatusActive('SENT')} onClick={() => applyStatusFilter('SENT')} />
                  <LegendItem label="Paid" value={statsLocal.byStatus.PAID} color={matte.green} active={isStatusActive('PAID')} onClick={() => applyStatusFilter('PAID')} />
                  <LegendItem label="All" value={statsLocal.totalCountStatusBasis} color={matte.grey} active={isStatusActive('ALL')} onClick={() => applyStatusFilter('ALL')} />
                </div>
              </div>
              {/* Source Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Source Breakdown</div>
                <SegmentBar segments={[
                  { value: statsLocal.bySource.DIRECT, color: matte.teal, title: 'Direct', onClick: () => applySourceFilter('DIRECT') },
                  { value: statsLocal.bySource.QUOTE, color: matte.amber, title: 'Quote', onClick: () => applySourceFilter('QUOTE') },
                  { value: statsLocal.bySource.RECURRING, color: matte.green, title: 'Recurring', onClick: () => applySourceFilter('RECURRING') },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Direct" value={statsLocal.bySource.DIRECT} color={matte.teal} active={isSourceActive('DIRECT')} onClick={() => applySourceFilter('DIRECT')} />
                  <LegendItem label="Quote" value={statsLocal.bySource.QUOTE} color={matte.amber} active={isSourceActive('QUOTE')} onClick={() => applySourceFilter('QUOTE')} />
                  <LegendItem label="Recurring" value={statsLocal.bySource.RECURRING} color={matte.green} active={isSourceActive('RECURRING')} onClick={() => applySourceFilter('RECURRING')} />
                  <LegendItem label="All" value={statsLocal.totalCountSourceBasis} color={matte.grey} active={isSourceActive('ALL')} onClick={() => applySourceFilter('ALL')} />
                </div>
              </div>
              {/* Amounts Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Amounts (INR)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', fontSize: 12 }}><span>Total</span><span style={{ fontWeight: 600 }}>{formatINR(totalAmt)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: matte.green, fontSize: 12 }}><span>Paid</span><span style={{ fontWeight: 600 }}>{formatINR(paidAmt)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: matte.blue, fontSize: 12 }}><span>Pending</span><span style={{ fontWeight: 600 }}>{formatINR(pendingAmt)}</span></div>
                </div>
                <SegmentBar segments={[
                  { value: paidAmt, color: matte.green, title: 'Paid' },
                  { value: pendingAmt, color: matte.blue, title: 'Pending' },
                ]} />
              </div>
            </div>
          </div>
        </div>
      )}
      {!showStats && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button style={styles.btnSecondary} onClick={() => setShowStats(true)}><FiChevronDown size={14} /> Show Stats</button>
        </div>
      )}

      {/* Filters */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={14} style={styles.searchIcon} />
              <input style={styles.searchInput} placeholder="Proforma number..." value={filters.number} onChange={e => { setFilters(f => ({ ...f, number: e.target.value })); setPage(1); }} />
            </div>
            <select style={styles.select} value={statusView} onChange={e => { setStatusView(e.target.value); setFilters(f => ({ ...f, status: e.target.value })); }}>
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button
            type="button"
            style={{ ...styles.btnSecondary, fontWeight: 400 }}
            onClick={() => { setFilters(f => ({ ...f, number: '', status: '' })); setStatusView(''); setSourceView(''); setPage(1); }}
          >
            <FiX size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table className="billing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Number</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </td></tr>
              )}
              {!loading && !visibleRows.length && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: colors.stone400, fontSize: 12 }}>No proformas found</td></tr>
              )}
              {!loading && visibleRows.map(r => {
                const unpaidAdvance = r.quoteId && String(r.paymentStatus || '').toUpperCase() !== 'PAID';
                return (
                  <tr key={r.id} className="billing-row" style={{ transition: 'background 0.15s', ...(unpaidAdvance ? { borderLeft: `3px solid ${colors.rose600}` } : {}) }}>
                    <td style={{ ...styles.td, fontWeight: 600 }}><button style={styles.linkBtn} onClick={() => viewPdf(r.id, r.number)}>{r.number}</button></td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{r.date}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>
                      {(r.customerSnapshot?.id || r.customerId) ? (
                        <button style={styles.linkBtn} onClick={() => openCustomerDetail(r)}>{r.customerSnapshot?.name || r.customer?.name || 'Customer'}</button>
                      ) : (r.customerSnapshot?.name || r.customer?.name || '-')}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12 }}>
                      {r.recurringInvoiceId ? (
                        <button style={{ ...styles.linkBtn, color: colors.emerald600 }} onClick={() => openRecurringFormInSideModal(r.recurringInvoiceId, r.number)}>Recurring</button>
                      ) : r.quoteId ? (
                        <button style={{ ...styles.linkBtn, color: colors.amber600 }} onClick={() => openQuotePdfById(r.quoteId, r.quoteNumber ? `Quote - ${r.quoteNumber}` : 'Quote')}>{r.quoteNumber ? `Quote ${r.quoteNumber}` : 'Quote'}</button>
                      ) : <span style={{ ...styles.badge, ...getSourceBadge(r) }}>{getSourceBadge(r).label}</span>}
                    </td>
                    <td style={styles.td}>
                      {['PAID', 'CONVERTED'].includes(String(r.status || '').toUpperCase()) ? (
                        <button style={{ ...styles.linkBtn, color: colors.stone700 }} onClick={() => openPaymentInfo(r)}>
                          <span style={styles.statusWithDot}>
                            <span style={styles.statusDot(getStatusDotColor(r.status))} />
                            {r.status}
                          </span>
                        </button>
                      ) : (
                        <span style={styles.statusWithDot}>
                          <span style={styles.statusDot(getStatusDotColor(r.status))} />
                          {r.status || '-'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontFeatureSettings: '"tnum"' }}>{formatINR(r.total)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        {!isProformaPaid(r) && (
                          <Link to={`/proforma/${r.id}/edit`} style={styles.btnIcon} title="Edit proforma">
                            <FiEdit2 size={14} />
                          </Link>
                        )}
                        {!isProformaPaid(r) && (
                          <button
                            style={{ ...styles.btnIcon, color: colors.stone600 }}
                            onClick={() => handleRegeneratePdf(r)}
                            disabled={!!actionLoading[`regen-pdf:${r.id}`]}
                            title="Regenerate PDF"
                          >
                            {actionLoading[`regen-pdf:${r.id}`] ? (
                              <span style={{ display: 'inline-block', width: 14, height: 14, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            ) : (
                              <FiRefreshCw size={14} />
                            )}
                          </button>
                        )}
                        {r.status !== 'CONVERTED' && String(r.paymentStatus || '').toUpperCase() !== 'PAID' && (
                          <button style={{ ...styles.btnIcon, color: colors.rose600 }} onClick={() => openPayModal(r)} disabled={!!actionLoading[`pay:${r.id}`]} title="Record payment"><FiCreditCard size={14} /></button>
                        )}
                        <button style={{ ...styles.btnIcon, color: colors.blue600 }} onClick={() => sendEmail(r.id)} disabled={!!actionLoading[`send:${r.id}`]} title="Send email"><FiMail size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </div>

      {/* PDF Preview Drawer */}
      {pdfPreview.open && (
        <div style={styles.drawer} onClick={closePdfPreview}>
          <div style={styles.drawerContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>{pdfPreview.kind} - {pdfPreview.label}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {pdfPreview.kind === 'Proforma' && pdfPreview.proformaId && (() => {
                  const previewRow = rows.find((x) => x.id === pdfPreview.proformaId);
                  return !previewRow || !isProformaPaid(previewRow);
                })() && (
                  <button
                    style={styles.btnSecondary}
                    onClick={() => handleRegeneratePdf(rows.find((x) => x.id === pdfPreview.proformaId) || { id: pdfPreview.proformaId })}
                    disabled={!!actionLoading[`regen-pdf:${pdfPreview.proformaId}`]}
                  >
                    <FiRefreshCw size={14} /> Regenerate PDF
                  </button>
                )}
                {!!pdfPreview.url && <a href={pdfPreview.url} target="_blank" rel="noreferrer" style={{ ...styles.btnSecondary, textDecoration: 'none' }}><FiExternalLink size={14} /> Open</a>}
                <button style={styles.btnSecondary} onClick={closePdfPreview}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, background: colors.stone100 }}>
              {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              ) : pdfPreview.url ? (
                <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : <div style={{ padding: 20, color: colors.stone500 }}>No preview</div>}
            </div>
          </div>
        </div>
      )}

      {/* Customer Drawer */}
      {customerDrawer.open && (
        <div style={styles.drawer} onClick={closeCustomerDrawer}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Customer</h3>
              <button style={styles.btnSecondary} onClick={closeCustomerDrawer}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <CustomerDetail embeddedCustomerId={customerDrawer.id} embeddedCustomer={customerDrawer.customer} />
            </div>
          </div>
        </div>
      )}

      {/* Payment Info Modal */}
      {payInfo.open && (
        <div style={styles.modal} onClick={closePayInfo}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Payment Details</h3>
              <button style={styles.btnIcon} onClick={closePayInfo}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              {payInfo.loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.stone500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              {!!payInfo.error && !payInfo.loading && (
                <div style={{ background: colors.stone100, color: colors.stone700, padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>{payInfo.error}</div>
              )}
              {!payInfo.loading && payInfo.proforma && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><div style={styles.label}>Proforma</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{payInfo.proforma.number || `#${payInfo.proforma.id}`}</div></div>
                  <div><div style={styles.label}>Status</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.proforma.paymentStatus || payInfo.proforma.status || '-'}</div></div>
                  <div><div style={styles.label}>Method</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.proforma.paymentMethod || '-'}</div></div>
                  <div><div style={styles.label}>Reference</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.proforma.paymentReference || '-'}</div></div>
                  <div><div style={styles.label}>Payment Date</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.proforma.paymentDate ? new Date(payInfo.proforma.paymentDate).toISOString().slice(0, 10) : '-'}</div></div>
                  <div><div style={styles.label}>Amount Paid</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{formatINR(payInfo.proforma.amountPaid || 0)}</div></div>
                  <div style={{ gridColumn: '1 / -1' }}><div style={styles.label}>Total</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{formatINR(payInfo.proforma.total || 0)}</div></div>
                  {!!payInfo.proforma.paymentProofUrl && (
                    <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                      <div style={styles.label}>Proof</div>
                      <a href={payInfo.proforma.paymentProofUrl} target="_blank" rel="noreferrer">
                        <img src={payInfo.proforma.paymentProofUrl} alt="Proof" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: `1px solid ${colors.stone200}` }} />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={closePayInfo}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Proforma Pay Modal */}
      <PaymentModal
        isOpen={proformaPayModal.open}
        onClose={() => setProformaPayModal({ open: false, proformaId: null, isAdvance: false, isRecurring: false })}
        onSubmit={submitProformaPayment}
        title={proformaPayModal.isRecurring ? 'Convert to Paid Invoice' : 'Record Payment'}
        submitText={proformaPayModal.isRecurring ? 'Create Paid Invoice' : 'Mark Paid'}
        submitLoadingText="Processing..."
        isSubmitting={!!actionLoading[`pay:${proformaPayModal.proformaId}`]}
        acceptPdfProof={true}
      />
    </div>
  );
}
